import PropTypes from "prop-types";
import { InputText as InputTextPR } from "primereact/inputtext";

import Label from "../Label";

/**
 * A text input field with an optional label, helper text, error message, and suffix addon.
 *
 * @param {object} props
 * @param {string} props.id - Unique identifier for the input; used to associate the label and ARIA descriptions.
 * @param {string} [props.className] - Additional CSS classes applied to the input group wrapper.
 * @param {string} [props.inputClassName] - Additional CSS classes applied to the input element itself (e.g. padding, font size).
 * @param {string} [props.error] - Error message displayed below the input; also marks the input as invalid.
 * @param {string} [props.helperText] - Hint text shown below the input when there is no error.
 * @param {string|RegExp} [props.keyFilter] - PrimeReact keyfilter preset or regex to restrict allowed characters.
 * @param {string} [props.label] - Text displayed as the input label.
 * @param {string} [props.maxLength] - Maximum number of characters allowed in the input.
 * @param {string} [props.name] - HTML name attribute for the input element.
 * @param {function} [props.onChange] - Called with the PrimeReact change event when the value changes.
 * @param {string} [props.placeholder] - Placeholder text shown when the input is empty.
 * @param {string} [props.suffixText] - Text rendered as an input group addon after the input field (e.g. a unit like "kg").
 * @param {string} [props.value] - The current value of the input.
 * @param {boolean} [props.disabled=false] - When true, disables the input and applies disabled styling.
 * @param {boolean} [props.isRequired=false] - When true, renders a required indicator on the label and sets `aria-required` on the input.
 */
const InputText = ({
  id,
  className,
  inputClassName,
  error,
  helperText,
  keyFilter,
  label,
  maxLength,
  name,
  onChange,
  placeholder,
  suffixText,
  value,
  disabled = false,
  isRequired = false,
}) => {
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const describedBy =
    [error ? errorId : null, !error && helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <section
      aria-labelledby={label ? labelId : undefined}
      className="flex flex-col gap-1 w-full"
    >
      {!!label && (
        <div id={labelId}>
          <Label htmlFor={id} text={label} isRequired={!!isRequired} />
        </div>
      )}
      <div className={`p-inputgroup flex-1 ${className ?? ""}`.trim()}>
        <InputTextPR
          id={id}
          aria-labelledby={label ? labelId : undefined}
          aria-required={isRequired || undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          readOnly={disabled}
          disabled={disabled}
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          invalid={(!!error).toString()}
          className={`!h-5 ${error ? "p-invalid " : ""}${inputClassName ?? ""}`.trim()}
          maxLength={maxLength}
          keyfilter={keyFilter}
        />
        {!!suffixText && (
          <span className="p-inputgroup-addon !py-0">{suffixText}</span>
        )}
      </div>
      {!!helperText && !error && (
        <small id={helperId} className="-mt-1 text-gray-500">
          {helperText}
        </small>
      )}
      {!!error && (
        <small id={errorId} role="alert" className="-mt-1 text-red-500">
          {error}
        </small>
      )}
    </section>
  );
};

InputText.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  keyFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
  label: PropTypes.string,
  maxLength: PropTypes.number,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  suffixText: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  isRequired: PropTypes.bool,
};

export default InputText;
